import React from 'react';
import clsx from 'clsx';
import { CheckCircle2, ArrowLeft, Sparkles } from 'lucide-react';

const TONES = {
    brand:     { glow: 'from-brand-100 via-surface to-surface',         icon: 'bg-brand-500 text-ink-inverse',         cta: 'bg-brand-500 hover:bg-brand-600 text-ink-inverse' },
    secondary: { glow: 'from-secondary-50 via-surface to-surface',      icon: 'bg-secondary-500 text-ink-inverse',     cta: 'bg-secondary-500 hover:bg-secondary-600 text-ink-inverse' },
    info:      { glow: 'from-info-50 via-surface to-surface',           icon: 'bg-info-500 text-ink-inverse',          cta: 'bg-info-500 hover:bg-info-600 text-ink-inverse' },
    warning:   { glow: 'from-warning-50 via-surface to-surface',        icon: 'bg-warning-500 text-white',             cta: 'bg-warning-500 hover:bg-warning-600 text-white' },
};

/**
 * Teaching empty state for admin tabs. Compared to the generic
 * <EmptyState>, this one explains *why* the feature matters and offers
 * concrete example use-cases — so an owner who lands on an empty tab
 * comes away knowing what the tool is for.
 *
 * Layout: warm gradient halo + big icon, headline, body, optional
 * "examples" bullet list, primary + optional secondary CTA.
 */
export function AdminTabEmpty({
    icon: Icon = Sparkles,
    tone = 'brand',
    title,
    body,
    examples = [],
    primaryAction,        // { label, onClick }
    secondaryAction,      // { label, onClick }
}) {
    const t = TONES[tone] || TONES.brand;

    return (
        <div className={clsx('relative overflow-hidden rounded-2xl border border-line-subtle bg-gradient-to-b', t.glow)}>
            <div className="relative px-6 py-12 sm:px-10 sm:py-16 text-center max-w-2xl mx-auto">
                <div className={clsx('mx-auto mb-6 w-20 h-20 rounded-2xl flex items-center justify-center shadow-md', t.icon)}>
                    <Icon className="w-10 h-10" />
                </div>

                <h3 className="text-2xl sm:text-3xl font-bold text-ink-strong mb-3 tracking-tight">
                    {title}
                </h3>

                {body && (
                    <p className="text-base text-ink-muted leading-relaxed mb-6">
                        {body}
                    </p>
                )}

                {examples?.length > 0 && (
                    <div className="text-start max-w-md mx-auto mb-7 bg-surface/70 rounded-xl border border-line-subtle p-4">
                        <div className="text-2xs font-bold tracking-wider text-ink-faint uppercase mb-2">
                            כמה דוגמאות
                        </div>
                        <ul className="space-y-2">
                            {examples.map((ex, i) => (
                                <li key={i} className="flex items-start gap-2.5 text-sm text-ink leading-relaxed">
                                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-success-500 shrink-0" aria-hidden="true" />
                                    <span>{ex}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div className="flex flex-wrap items-center justify-center gap-2.5">
                    {primaryAction && (
                        <button
                            type="button"
                            onClick={primaryAction.onClick}
                            className={clsx(
                                'inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg font-semibold text-sm',
                                'transition-colors duration-200 shadow-sm',
                                'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
                                t.cta,
                            )}
                        >
                            {primaryAction.label}
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                    )}
                    {secondaryAction && (
                        <button
                            type="button"
                            onClick={secondaryAction.onClick}
                            className="inline-flex items-center px-4 py-2.5 rounded-lg font-medium text-sm text-ink-muted hover:text-ink hover:bg-surface-muted transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                        >
                            {secondaryAction.label}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export default AdminTabEmpty;
